import { jsx } from 'panel';
import partition from 'lodash/partition';
import sumBy from 'lodash/sumBy';
import { CheckboxEl } from 'common/components/checkbox';
import { TooltipEl } from 'common/components/tooltip';
import { SvgIconEl } from 'common/components/svg-icon';
import { Placement } from 'common/components/tooltip/types';
import { ButtonEl } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { SelectEl } from 'common/components/select';
import { SortBy, SortOrder } from 'common/charts/visualization/bar-viz/types';
import { SEGMENT_PREFIX } from './types';
import { isSameRowByContext } from './utils';
const DEFAULT_RESIZE_MIN_WIDTH = 65;
var ColumnSubset;

(function (ColumnSubset) {
  ColumnSubset["All"] = "all";
  ColumnSubset["Fixed"] = "fixed";
  ColumnSubset["Scrollable"] = "scrollable";
})(ColumnSubset || (ColumnSubset = {})); //#region helper mixins
// eslint-disable-next-line sonarjs/cognitive-complexity


function TitleCell(_ref) {
  let {
    column,
    tableEl,
    segmentationDataTable = false
  } = _ref;
  const {
    sortField,
    sortOrder,
    sorting
  } = tableEl.state;
  let isCurrSortField;

  if (segmentationDataTable) {
    isCurrSortField = sorting.sortBy === SortBy.Value && sorting.valueField === column.field;
  } else {
    isCurrSortField = sortField === column.field;
  }

  const isSegmentSelectCol = column.field.startsWith(SEGMENT_PREFIX) && tableEl.helpers.getNumSegments() > 1;
  const mouseHandlers = column.tooltip ? {
    mouseenter: ev => tableEl.helpers.handleHeaderMouseEnter(ev, column),
    mouseleave: tableEl.helpers.handleHeaderMouseLeave
  } : {};
  const headerHandlers = segmentationDataTable && isSegmentSelectCol ? {} : {
    click: () => tableEl.helpers.handleHeaderTitleClick(column)
  };
  let titleContents = null;

  if (column.title) {
    if (column.isSortable && segmentationDataTable) {
      if (isSegmentSelectCol) {
        titleContents = jsx(SelectEl, {
          attrs: tableEl.helpers.getSortSelectAttrs(column),
          on: {
            change: tableEl.helpers.handleSortSelect
          },
          props: {
            boundaryEl: tableEl.helpers.getSelectBoundaryEl()
          }
        }, jsx("div", {
          attrs: {
            slot: "trigger"
          },
          sel: '.sort-label.within-select-el',
          on: mouseHandlers
        }, jsx("div", {
          sel: '.text.within-select-el'
        }, column.title), jsx("div", {
          sel: '.sort-icon'
        }, jsx(SvgIconEl, {
          attrs: {
            icon: "caret-down"
          }
        }))));
      } else {
        titleContents = jsx("div", {
          sel: '.sort-label.value-sort',
          on: mouseHandlers
        }, jsx("div", {
          sel: '.text'
        }, column.title), jsx("div", {
          sel: '.sort-icon'
        }, isCurrSortField ? sorting.sortOrder === SortOrder.Ascending ? jsx(SvgIconEl, {
          attrs: {
            icon: "sort-value-asc"
          }
        }) : jsx(SvgIconEl, {
          attrs: {
            icon: "sort-value-desc"
          }
        }) : null));
      }
    } else {
      titleContents = jsx("div", {
        sel: '.mp-table-title-label',
        class: {
          'mp-table-title-currently-sorted-field': isCurrSortField,
          'mp-table-title-italics': column.italics
        },
        on: mouseHandlers
      }, jsx("div", {
        sel: '.text'
      }, column.title));
    }
  }

  return jsx("div", {
    sel: '.mp-table-cell.title-cell',
    class: {
      'mp-table-sortable-column': column.isSortable,
      'mp-checkbox-cell': column.type === "checkbox",
      'mp-text-cell': column.type === "text",
      'mp-number-cell': column.type === "number",
      'mp-table-flattened-segment-name': column.field === "header",
      'mp-table-segment-name': column.field.startsWith(SEGMENT_PREFIX),
      'mp-table-first-segment-name': column.field === "".concat(SEGMENT_PREFIX, "0"),
      'first-in-group': column.first && column.type !== "checkbox",
      'last-in-group': column.last && column.type !== "checkbox",
      'overall-conversion-header': column.field === "overall",
      'last-column': column.rightmost,
      'last-sticky': column.lastSticky
    },
    on: headerHandlers,
    style: Object.assign({}, tableEl.helpers.hasClickInteraction(column) ? {
      cursor: "pointer"
    } : {}),
    attrs: {
      title: column.label || ""
    }
  }, column.type === "checkbox" ? // checkbox-wrapper so we can increase click surface area
  jsx("div", {
    sel: '.mp-table-checkbox-wrapper',
    style: {
      pointerEvents: tableEl.attr("multi-select") ? "" : "none"
    },
    on: {
      click: tableEl.helpers.toggleAllSelected
    }
  }, tableEl.attr("multi-select") ? (() => {
    const {
      numSelected,
      allSelected
    } = tableEl.helpers.getSelectionStats();
    return jsx(CheckboxEl, {
      attrs: {
        checked: allSelected,
        'unchecked-icon': numSelected > 0 ? "dash" : ""
      }
    });
  })() : null) : null, titleContents, column.icon ? jsx("div", {
    sel: '.mp-table-title-cell-icon',
    class: {
      'mp-table-title-currently-sorted-field': isCurrSortField
    },
    on: mouseHandlers
  }, jsx(SvgIconEl, {
    attrs: {
      icon: column.icon
    }
  })) : null, column.isSortable && !segmentationDataTable ? jsx("div", {
    sel: '.mp-table-sort-controls'
  }, isCurrSortField ? sortOrder === 1 ? jsx(SvgIconEl, {
    attrs: {
      icon: "arrow-up"
    }
  }) : jsx(SvgIconEl, {
    attrs: {
      icon: "arrow-down"
    }
  }) : null) : null);
}

function CellTypeText(_ref2) {
  let {
    context
  } = _ref2;
  return context.label;
}
/**
 * Positions a header-cell tooltip correctly.
 *
 * The default mp-tooltip does some magic to position itself which works for
 * most cases, but not for our sticky cells in a scrolling container.
 *
 * This mixin renders the tooltip outside of the table scroll container and on top of
 * the cell it's meant to annotate. Then, mp-tooltip does its normal positioning.
 */


function TitleTooltip(_ref3) {
  let {
    tooltip
  } = _ref3;

  if (!tooltip) {
    return null;
  }

  const {
    left,
    text,
    top
  } = tooltip;
  return jsx("div", {
    sel: '.position-tooltip',
    style: {
      left: "".concat(left, "px"),
      top: "".concat(top, "px"),
      position: "absolute"
    }
  }, jsx(TooltipEl, {
    attrs: {
      'max-width': "250px",
      placement: Placement.Bottom,
      'always-show': true
    }
  }, text));
}

function CellTypeCheckbox(_ref4) {
  let {
    item,
    context,
    tableEl,
    isSubRow = false
  } = _ref4;
  const options = tableEl.helpers.getCheckboxState(item);
  return !context.isComparison ? jsx("div", {
    sel: '.mp-table-checkbox-wrapper',
    class: {
      disabled: options.attrs.disabled
    },
    on: {
      click: ev => !options.attrs.disabled && tableEl.helpers.toggleSelected(ev, item)
    }
  }, !isSubRow ? jsx(null, null, jsx(CheckboxEl, {
    attrs: options.attrs
  }), options.selectDisabledReason && options.attrs.disabled ? jsx(TooltipEl, {
    attrs: {
      placement: Placement.Right
    }
  }, options.selectDisabledReason) : null) : null) : null;
}

function CellTypeCollapsible(_ref5) {
  let {
    item,
    tableEl,
    isSubRow = false
  } = _ref5;
  return jsx("div", {
    sel: '.mp-table-collapsible-wrapper'
  }, !isSubRow ? jsx(ButtonEl, {
    attrs: {
      icon: item.isCollapsed ? "caret-right" : "caret-down",
      theme: ButtonTheme.Tertiary
    },
    on: {
      click: ev => tableEl.helpers.collapsibleClicked(ev, item)
    }
  }) : null);
}

function renderResizeHandles(_ref6) {
  let {
    dataTable,
    columns,
    gap,
    idxOffset,
    resizingColumnIdx
  } = _ref6;
  return columns.reduce((acc, column, idx) => {
    const {
      width,
      fixedWidth
    } = column;
    const minWidth = column.minWidth ? column.minWidth : DEFAULT_RESIZE_MIN_WIDTH;
    const handleWidth = gap ? gap : 8;
    const globalColIdx = idx + idxOffset;
    const offsetWidth = acc.offsetWidth + (idx === 0 ? 0 : gap) + width;
    const handleLeft = "".concat(offsetWidth - (gap ? 0 : handleWidth / 2), "px");
    const isResizing = resizingColumnIdx === globalColIdx;
    return {
      offsetWidth,
      nodes: fixedWidth ? acc.nodes : acc.nodes.concat(jsx("div", {
        sel: ".column-resize-handle",
        class: {
          'is-resizing': isResizing,
          'other-resizing': Boolean(resizingColumnIdx) && !isResizing
        },
        style: {
          left: handleLeft,
          width: "".concat(handleWidth, "px")
        },
        on: {
          pointerdown: ev => dataTable.onResizePointerDown({
            ev,
            resizeColumnIndex: globalColIdx,
            initialWidth: width,
            minWidth
          })
        }
      }))
    };
  }, {
    offsetWidth: 0,
    nodes: []
  }).nodes;
} //#endregion


function getWidth(cols, gap) {
  return sumBy(cols, c => c.width) + gap * (cols.length - 1);
}

function getTemplateWidths(cols) {
  return cols.map(c => "".concat(c.width, "px")).join(" ");
} // eslint-disable-next-line sonarjs/cognitive-complexity


export function template() {
  const segmentationDataTable = this.attr("segmentation-data-table");
  const tableTheme = this.getTheme();
  const gap = tableTheme.cellBorders === "gap" ? 5 : 0;
  const columns = this.helpers.getColumnsToDisplay();
  const [fixedCols, dataCols] = partition(columns, col => col.sticky);
  const rows = this.helpers.getRowsToDisplay();
  const fixedColumnWidth = getWidth(fixedCols, gap);
  const dataColumnWidth = getWidth(dataCols, gap);

  if (segmentationDataTable) {
    const autoRowsStyle = {
      'grid-auto-rows': "".concat(tableTheme.rowHeight, "px")
    };
    const scrollContainerWidth = this.state.tableWidth ? "".concat(this.state.tableWidth - fixedColumnWidth - gap * 2, "px") : "100%";
    return (// wrap the table in order to overlay tooltips on the scrollbox
      jsx("div", {
        sel: '.wrapper'
      }, jsx("div", {
        sel: '.mp-data-table',
        style: {
          '--data-table-columns': "".concat(fixedColumnWidth, "px ").concat(dataColumnWidth, "px"),
          '--gap': "".concat(gap, "px"),
          '--height': this.attr("max-body-height")
        },
        class: {
          'use-borders': tableTheme.cellBorders === "line",
          'header-shadow': tableTheme.cellHeaderShadow,
          'rounded-cells': tableTheme.cellShape === "round",
          'cell-outline-on-hover': tableTheme.cellBorderHover === "outline"
        },
        on: {
          scroll: this.helpers.handleScroll
        },
        hook: {
          insert: vnode => this.tableInserted(vnode.elm)
        }
      }, jsx("div", {
        sel: '.fixed-columns',
        style: Object.assign({
          '--fixed-column-sizes': getTemplateWidths(fixedCols)
        }, autoRowsStyle)
      }, fixedCols.map(col => jsx(TitleCell, {
        column: col,
        segmentationDataTable: segmentationDataTable,
        tableEl: this
      })), rows.map((row, i) => jsx(RowGroup, {
        row: row,
        rowIdx: i,
        columnSubset: ColumnSubset.Fixed,
        tableEl: this
      })), renderResizeHandles({
        dataTable: this,
        columns: fixedCols,
        gap,
        idxOffset: 0,
        resizingColumnIdx: this.state.resizeColumnIndex
      })), jsx("div", {
        sel: '.scrollable-columns',
        style: Object.assign({
          width: scrollContainerWidth,
          '--data-column-sizes': getTemplateWidths(dataCols)
        }, autoRowsStyle),
        class: {
          'is-resizing': Boolean(this.state.resizeColumnIndex)
        }
      }, dataCols.map(col => jsx(TitleCell, {
        column: col,
        segmentationDataTable: segmentationDataTable,
        tableEl: this
      })), rows.map((row, i) => jsx(RowGroup, {
        row: row,
        rowIdx: i,
        columnSubset: ColumnSubset.Scrollable,
        tableEl: this
      })), renderResizeHandles({
        dataTable: this,
        columns: dataCols,
        gap,
        idxOffset: fixedCols.length,
        resizingColumnIdx: this.state.resizeColumnIndex
      })), jsx("div", {
        hook: {
          insert: vnode => this.helpers.setIntersectionEl(vnode.elm)
        },
        style: {
          position: "relative",
          bottom: "400px",
          width: "100%"
        }
      })), jsx(TitleTooltip, {
        tooltip: this.state.cellToolTip
      }))
    );
  } else {
    return (// wrap the table in order to overlay tooltips on the scrollbox
      jsx("div", {
        sel: '.wrapper'
      }, jsx("div", {
        sel: '.mp-table',
        style: {
          '--data-table-columns': "".concat(fixedColumnWidth, "px ").concat(dataColumnWidth, "px"),
          '--fixed-column-sizes': getTemplateWidths(fixedCols),
          '--data-column-sizes': getTemplateWidths(dataCols),
          '--gap': "".concat(gap, "px"),
          '--height': this.attr("max-body-height"),
          '--first-column-group-width': "".concat(fixedColumnWidth + gap, "px")
        },
        class: {
          'use-borders': tableTheme.cellBorders === "line",
          'header-shadow': tableTheme.cellHeaderShadow,
          'rounded-cells': tableTheme.cellShape === "round",
          'cell-outline-on-hover': tableTheme.cellBorderHover === "outline"
        },
        on: {
          scroll: this.helpers.handleScroll
        }
      }, jsx("div", {
        sel: '.table-header.mp-data-table-grid'
      }, jsx("div", {
        sel: '.fixed-columns-outer'
      }, fixedCols.map(col => jsx(TitleCell, {
        column: col,
        tableEl: this
      }))), jsx("div", {
        sel: '.data-columns-outer'
      }, dataCols.map(col => jsx(TitleCell, {
        column: col,
        tableEl: this
      })))), rows.map((row, i) => jsx(RowGroup, {
        row: row,
        rowIdx: i,
        columnSubset: ColumnSubset.All,
        tableEl: this
      })), jsx("div", {
        hook: {
          insert: vnode => this.helpers.setIntersectionEl(vnode.elm)
        },
        style: {
          position: "relative",
          bottom: "400px",
          width: "100%"
        }
      })), jsx(TitleTooltip, {
        tooltip: this.state.cellToolTip
      }))
    );
  }

  function RowGroup(_ref7) {
    let {
      row,
      rowIdx,
      columnSubset,
      tableEl
    } = _ref7;

    var _a;

    const currRowContexts = tableEl.helpers.getCellRenderContexts(tableEl.helpers.getColumnsToDisplay(), row, rowIdx, 1);
    let expandedRows = [];

    if (!row.isCollapsed) {
      expandedRows = (_a = row.groupedRows) !== null && _a !== void 0 ? _a : [];
    }

    return jsx(null, null, jsx(RowEl, {
      row: row,
      rowIdx: rowIdx,
      columnSubset: columnSubset,
      tableEl: tableEl,
      currRowContexts: currRowContexts
    }), expandedRows.map((row, rowSubIdx) => jsx(RowEl, {
      row: row,
      rowIdx: rowSubIdx,
      columnSubset: columnSubset,
      tableEl: tableEl,
      currRowContexts: currRowContexts.map(c => Object.assign(Object.assign({}, c), {
        groupedRowIdx: rowSubIdx
      })),
      isSubRow: true
    })));
  }

  function RowEl(_ref8) {
    let {
      row,
      rowIdx,
      columnSubset,
      tableEl,
      currRowContexts,
      isSubRow = false
    } = _ref8;

    if (columnSubset === ColumnSubset.All) {
      const autoRowsStyle = {
        'grid-auto-rows': "".concat(tableTheme.rowHeight, "px")
      };
      return jsx("div", {
        sel: '.row.mp-data-table-grid',
        class: {
          'first-row': rowIdx === 0 && !isSubRow
        }
      }, jsx("div", {
        sel: '.fixed-columns-outer',
        style: Object.assign({}, autoRowsStyle)
      }, fixedCols.map((col, i) => jsx(Cell, {
        column: col,
        row: row,
        rowIdx: rowIdx,
        columnIdx: i,
        tableEl: tableEl,
        contexts: currRowContexts,
        isSubRow: isSubRow
      }))), jsx("div", {
        sel: '.data-columns-outer',
        style: Object.assign({}, autoRowsStyle)
      }, dataCols.map((col, i) => jsx(Cell, {
        column: col,
        row: row,
        rowIdx: rowIdx,
        columnIdx: i + fixedCols.length,
        tableEl: tableEl,
        contexts: currRowContexts,
        isSubRow: isSubRow
      }))));
    } else if (columnSubset === ColumnSubset.Fixed) {
      return jsx(null, null, fixedCols.map((col, i) => jsx(Cell, {
        column: col,
        row: row,
        rowIdx: rowIdx,
        columnIdx: i,
        tableEl: tableEl,
        contexts: currRowContexts,
        isSubRow: isSubRow,
        newTableStructure: true
      })));
    } else {
      return jsx(null, null, dataCols.map((col, i) => jsx(Cell, {
        column: col,
        row: row,
        rowIdx: rowIdx,
        columnIdx: i + fixedCols.length,
        tableEl: tableEl,
        contexts: currRowContexts,
        isSubRow: isSubRow,
        newTableStructure: true
      })));
    }
  }

  function Cell(_ref9) {
    let {
      row,
      rowIdx,
      column,
      columnIdx,
      tableEl,
      contexts,
      isSubRow = false,
      newTableStructure = false
    } = _ref9;
    const context = contexts[columnIdx];
    const cellItem = tableEl.helpers.getCellItem(row, columnIdx);
    const evenRow = rowIdx % 2 === 0;
    const empty = cellItem === null;
    const bgColor = cellItem && cellItem.backgroundColor || column.backgroundColor || null;
    const textColor = cellItem && cellItem.textColor || column.textColor || null;
    const notFocusable = !tableTheme.cellFocusable || tableTheme.cellFocusable && empty;
    let cellContent;

    if (column.type === "checkbox") {
      cellContent = jsx(CellTypeCheckbox, {
        context: context,
        item: row,
        tableEl: tableEl,
        isSubRow: isSubRow
      });
    } else if (column.type === "collapsible") {
      cellContent = jsx(CellTypeCollapsible, {
        item: row,
        tableEl: tableEl,
        isSubRow: isSubRow
      });
    } else {
      cellContent = (column.cellRenderer || CellTypeText)({
        item: row,
        context
      }); // funnels statSig cells don't play well with .overflow-wrapper (causes the clickable icon to stack above text)

      if (newTableStructure && column.field !== "statSig") {
        cellContent = jsx("div", {
          sel: '.overflow-wrapper'
        }, cellContent);
      }
    }

    const canHaveHoverBackground = tableTheme.cellBackground === "focus-on-hover";
    return jsx("div", {
      sel: '.mp-table-cell',
      class: {
        'first-in-group': column.first && column.type !== "checkbox",
        'last-in-group': column.last && column.type !== "checkbox",
        'last-column': column.rightmost,
        'last-sticky': column.lastSticky,
        'mp-table-flattened-segment-name': column.field === "header",
        'mp-table-segment-name': column.field.startsWith(SEGMENT_PREFIX),
        'mp-table-first-segment-name': column.field === "".concat(SEGMENT_PREFIX, "0"),
        'mp-checkbox-cell': column.type === "checkbox",
        'mp-collapsible-cell': column.type === "collapsible",
        'mp-text-cell': column.type === "text",
        'mp-number-cell': column.type === "number",
        'mp-heatmap-cell': tableTheme.cellBackground === "heatmap",
        'has-multi-select': tableEl.attr("multi-select"),
        'first-in-pair': tableEl.attr("has-comparison") && evenRow,
        'hovered-row': canHaveHoverBackground ? isSameRowByContext(tableEl.state.hoveredContext, context) : false,
        'first-row': rowIdx === 0 && !isSubRow,
        'is-empty': empty,
        selectable: column.isSelectable
      },
      attrs: Object.assign({}, notFocusable ? {} : {
        tabindex: 0
      }),
      style: {
        'background-color': bgColor,
        color: textColor,
        cursor: cellItem && cellItem.clickable ? "pointer" : "auto"
      },
      on: Object.assign({
        click: ev => tableEl.helpers.handleCellClick(ev, row, column.field, context)
      }, canHaveHoverBackground ? {
        pointerenter: () => tableEl.helpers.handleCellPointerEnter(context),
        pointerleave: () => tableEl.helpers.handleCellPointerLeave()
      } : {})
    }, cellContent, cellItem && cellItem.tooltip ? (() => {
      const placement = columnIdx >= 8 ? Placement.Left : Placement.Right;
      return jsx(TooltipEl, {
        attrs: {
          placement
        },
        style: {
          'z-index': "200"
        }
      }, cellItem.tooltip);
    })() : null);
  }
}